import { HttpErrorResponse } from '@angular/common/http';
import { computed, inject, Injectable, signal } from '@angular/core';
import { Router } from '@angular/router';
import { Observable, of, throwError } from 'rxjs';
import { catchError, map, switchMap } from 'rxjs/operators';

import { EnvironmentService } from '@core/config/environment.service';
import { ApiService } from '@core/http/api.service';
import { AuthEventsService } from '@core/services/auth-events.service';
import { LoggerService } from '@core/services/logger.service';
import { TokenStorageService } from '@core/services/token-storage.service';

import type {
  AuthTokens,
  LoginOutcome,
  LoginPayload,
  SessionUser,
  TermsVerificationOutcome,
} from './auth.models';
import { extractTokens, readApiErrorCode, resolveMfaChallenge } from './auth-login-response.util';
import { sanitizeLoginErrorMessage } from './login-error-message.util';
import { decodeJwtPayload, isRecognizedAccessToken, rolesFromPayload } from './jwt.util';

function authUsesRefreshCookie(env: { auth: { refreshTokenTransport: string } }): boolean {
  return env.auth.refreshTokenTransport === 'httpOnlyCookie';
}

/** Default route after sign-in and for authenticated guests hitting `/login`. */
export const DEFAULT_AUTHENTICATED_ROUTE = '/';

const LOGIN_DEVICE_STRING_MAX = 256;

function loginDeviceString(): string {
  if (typeof navigator === 'undefined' || !navigator.userAgent) {
    return '';
  }
  const ua = navigator.userAgent.trim();
  return ua.length > LOGIN_DEVICE_STRING_MAX ? ua.slice(0, LOGIN_DEVICE_STRING_MAX) : ua;
}

/**
 * Body for `POST /auth/login`. Deployed apps must use HTTPS end-to-end (HSTS, redirect
 * from HTTP, secure cookies) so credentials are never sent in clear text. `device` is a truncated
 * user-agent string for session/fraud signals — see the Privacy Policy.
 */
function buildLoginBody(payload: LoginPayload): Record<string, unknown> {
  const body: Record<string, unknown> = {
    email: payload.email,
    password: payload.password,
    device: loginDeviceString(),
    timezone: Intl.DateTimeFormat().resolvedOptions().timeZone,
  };
  const otp = payload.otp?.trim();
  if (otp) {
    body['otp'] = otp;
  }
  return body;
}

@Injectable({ providedIn: 'root' })
export class AuthService {
  private readonly api = inject(ApiService);
  private readonly tokens = inject(TokenStorageService);
  private readonly env = inject(EnvironmentService);
  private readonly logger = inject(LoggerService);
  private readonly router = inject(Router);
  private readonly authEvents = inject(AuthEventsService);

  private readonly userSignal = signal<SessionUser | null>(null);

  /** Current session derived from the access token (JWT payload). */
  readonly user = this.userSignal.asReadonly();
  readonly roles = computed(() => this.userSignal()?.roles ?? []);
  readonly isAuthenticated = computed(() => this.userSignal() !== null);

  constructor() {
    this.hydrateFromStorage();
    this.authEvents.unauthorized$.subscribe(() => this.clearSession());
  }

  /**
   * Runs once at startup (APP_INITIALIZER): restores an access token from memory, or
   * silently refreshes using `sessionStorage` refresh (`body`) or HttpOnly cookie (`httpOnlyCookie`).
   */
  hydrateSessionOnStartup(): Observable<void> {
    const access = this.tokens.getAccessToken();
    if (access && isRecognizedAccessToken(access)) {
      this.userSignal.set(this.sessionFromAccessToken(access));
      return of(undefined);
    }

    if (authUsesRefreshCookie(this.env)) {
      return this.refreshSession().pipe(
        map(() => undefined),
        catchError(() => of(undefined)),
      );
    }

    if (!this.tokens.getRefreshToken()) {
      return of(undefined);
    }

    return this.refreshSession().pipe(
      map(() => undefined),
      catchError(() => {
        this.tokens.clearRefreshToken();
        return of(undefined);
      }),
    );
  }

  /**
   * For route guards: use a valid access token, or try one silent refresh when refresh credentials exist.
   */
  ensureAuthenticatedForNavigation(): Observable<boolean> {
    if (this.hasValidSession()) {
      if (!this.userSignal()) {
        this.hydrateFromStorage();
      }
      return of(true);
    }
    this.clearIfInvalid();
    const canTryRefresh = authUsesRefreshCookie(this.env) || !!this.tokens.getRefreshToken();
    if (!canTryRefresh) {
      return of(false);
    }
    return this.refreshSession().pipe(
      map(() => this.hasValidSession()),
      catchError(() => of(false)),
    );
  }

  /** POST forgot-password / reset-request — backend contract may vary. */
  requestPasswordReset(payload: { email: string }): Observable<unknown> {
    const path = this.env.auth.endpoints.forgotPassword;
    if (!path) {
      return throwError(() => new Error('Password reset is not configured'));
    }
    return this.api.post<unknown>(path, payload).pipe(
      catchError((err: unknown) => {
        this.logger.warn('Password reset request failed', err);
        return throwError(() => err);
      }),
    );
  }

  /**
   * Credentials step — `POST /auth/login` without OTP. Returns MFA challenge or immediate auth.
   */
  verifySignInTerms(payload: LoginPayload): Observable<TermsVerificationOutcome> {
    return this.postLogin(payload).pipe(
      map((outcome) => {
        if (outcome.outcome === 'mfa_required') {
          return this.toTermsVerificationOutcome(outcome);
        }
        if (outcome.outcome === 'authenticated') {
          return { outcome: 'authenticated', tokens: outcome.tokens } satisfies TermsVerificationOutcome;
        }
        return { outcome: 'terms_verified' } satisfies TermsVerificationOutcome;
      }),
      catchError((err: unknown) => {
        this.logger.warn('Sign-in failed', err);
        return throwError(() => err);
      }),
    );
  }

  /**
   * Completes sign-in with `POST /auth/login` — call from the OTP step (with code) or for non-MFA
   * accounts when the user continues without a code.
   */
  completeSignIn(payload: LoginPayload): Observable<LoginOutcome> {
    return this.postLogin(payload);
  }

  /**
   * @deprecated Prefer {@link verifySignInTerms} on the credentials step and {@link completeSignIn} on OTP.
   */
  signIn(payload: LoginPayload): Observable<LoginOutcome> {
    if (payload.otp?.trim()) {
      return this.completeSignIn(payload);
    }
    return this.verifySignInTerms(payload).pipe(
      switchMap((result) => {
        if (result.outcome === 'mfa_required') {
          return of({
            outcome: 'mfa_required',
            authenticationType: result.authenticationType,
            emailHint: result.emailHint,
          } satisfies LoginOutcome);
        }
        if (result.outcome === 'authenticated') {
          return of({ outcome: 'authenticated', tokens: result.tokens } satisfies LoginOutcome);
        }
        return this.completeSignIn(payload);
      }),
    );
  }

  private toTermsVerificationOutcome(
    mfa: Extract<LoginOutcome, { outcome: 'mfa_required' }>,
  ): TermsVerificationOutcome {
    return {
      outcome: 'mfa_required',
      authenticationType: mfa.authenticationType,
      emailHint: mfa.emailHint,
    };
  }

  private postLogin(payload: LoginPayload): Observable<LoginOutcome> {
    return this.api
      .post<unknown>(this.env.auth.endpoints.login, buildLoginBody(payload), {
        ...(authUsesRefreshCookie(this.env) ? { withCredentials: true } : {}),
      })
      .pipe(
        map((body) => this.mapLoginResponseToOutcome(body)),
        catchError((err: unknown) => {
          this.logger.warn('Login failed', err);
          return throwError(() => err);
        }),
      );
  }

  private parseHttpErrorBody(body: unknown): Record<string, unknown> {
    if (body === null || body === undefined) {
      return {};
    }
    if (typeof body === 'string') {
      try {
        const parsed = JSON.parse(body) as unknown;
        return this.parseHttpErrorBody(parsed);
      } catch {
        return { message: body };
      }
    }
    if (typeof body !== 'object') {
      return {};
    }
    return body as Record<string, unknown>;
  }

  private mapLoginResponseToOutcome(body: unknown): LoginOutcome {
    if (!body || typeof body !== 'object') {
      throw new Error('Invalid login response');
    }
    const challenge = resolveMfaChallenge(body);
    if (challenge) {
      return challenge;
    }
    const parsed = extractTokens(body);
    if (parsed?.accessToken) {
      this.persistTokens(parsed);
      return { outcome: 'authenticated', tokens: parsed };
    }
    throw new Error('Login response missing access token');
  }

  /** Maps HTTP / API errors to user-facing copy — never pass through stack traces or internal diagnostics. */
  mapLoginError(err: unknown): string {
    const generic = 'Sign-in failed. Check credentials or API configuration.';
    if (err instanceof HttpErrorResponse) {
      const record = this.parseHttpErrorBody(err.error);
      const code = readApiErrorCode(record);
      if (code === 'INVALID_OTP' || code === 'MFA_OTP_INVALID') {
        return 'OTP wrong';
      }
      if (code === 'AUTH_INVALID_CREDENTIALS') {
        return 'Email or password is not correct';
      }
      if (code === 'RATE_LIMIT_EXCEEDED' || err.status === 429) {
        return 'Too many attempts. Please wait a moment and try again.';
      }

      const msg = record['message'];
      if (typeof msg === 'string' && msg) {
        const safe = sanitizeLoginErrorMessage(msg);
        if (safe) {
          return safe;
        }
      }
      if (err.status === 401) {
        return 'Email or password is not correct';
      }
    }
    if (err instanceof Error && err.message) {
      const safe = sanitizeLoginErrorMessage(err.message);
      if (safe) {
        return safe;
      }
    }
    return generic;
  }

  logout(options?: { callServer?: boolean }): void {
    const callServer = options?.callServer ?? true;
    if (!callServer) {
      this.clearSession();
      return;
    }
    const cookieMode = authUsesRefreshCookie(this.env);
    const rt = cookieMode ? null : this.tokens.getRefreshToken();
    const body = cookieMode ? {} : rt ? { refreshToken: rt } : {};
    const postOpts = cookieMode ? { withCredentials: true as const } : undefined;
    this.api.post(this.env.auth.endpoints.logout, body, postOpts).subscribe({
      next: () => this.clearSession(),
      error: () => this.clearSession(),
    });
  }

  /**
   * Refresh session using stored refresh token.
   * Called by the auth interceptor on 401 responses to recover expired access tokens.
   */
  refreshSession(): Observable<AuthTokens> {
    const cookieMode = authUsesRefreshCookie(this.env);

    if (!cookieMode) {
      const refresh = this.tokens.getRefreshToken();
      if (!refresh) {
        return throwError(() => new Error('No refresh token'));
      }
      return this.api.post<unknown>(this.env.auth.endpoints.refresh, { refreshToken: refresh }).pipe(
        map((body) => {
          const parsed = extractTokens(body);
          if (!parsed) {
            throw new Error('Refresh response missing access token');
          }
          this.persistTokens(parsed);
          return parsed;
        }),
        catchError((err: unknown) => {
          this.logger.warn('Refresh failed', err);
          return throwError(() => err);
        }),
      );
    }

    return this.api.post<unknown>(this.env.auth.endpoints.refresh, {}, { withCredentials: true }).pipe(
      map((body) => {
        const parsed = extractTokens(body);
        if (!parsed) {
          throw new Error('Refresh response missing access token');
        }
        this.persistTokens(parsed);
        return parsed;
      }),
      catchError((err: unknown) => {
        this.logger.warn('Refresh failed', err);
        return throwError(() => err);
      }),
    );
  }

  clearSession(options?: { navigateToLogin?: boolean }): void {
    this.tokens.clearAllTokens();
    this.userSignal.set(null);
    const navigate = options?.navigateToLogin ?? true;
    if (navigate) {
      void this.router.navigate(['/login']);
    }
  }

  /**
   * Re-checks in-memory tokens and clears the session if they are missing or malformed.
   * Expiration is handled by the API (401) and refresh flow to avoid clock-skew false logouts.
   */
  syncSessionFromStorage(): void {
    this.hydrateFromStorage();
  }

  /**
   * Synchronous auth check for guards/directives.
   * Uses the access token only and does not mutate session state.
   */
  hasValidSession(): boolean {
    return this.isAccessTokenValid();
  }

  /** Pure access-token check for guards/directives. */
  isAccessTokenValid(): boolean {
    const access = this.tokens.getAccessToken();
    return !!access && isRecognizedAccessToken(access);
  }

  /**
   * Explicitly clears invalid access-token state while preserving refresh credentials so silent
   * refresh can still recover the session.
   */
  clearIfInvalid(): void {
    if (this.isAccessTokenValid()) {
      if (!this.userSignal()) {
        this.hydrateFromStorage();
      }
      return;
    }
    this.tokens.clearAccessToken();
    this.userSignal.set(null);
  }

  navigateAfterLogin(returnUrl: string | null): void {
    const navOptions = { replaceUrl: true } as const;
    if (returnUrl && typeof window !== 'undefined') {
      try {
        const origin = window.location.origin;
        const target = new URL(returnUrl, origin);
        if (target.protocol !== 'https:' && target.protocol !== 'http:') {
          void this.router.navigateByUrl(DEFAULT_AUTHENTICATED_ROUTE, navOptions);
          return;
        }
        if (target.origin === origin) {
          const localUrl = `${target.pathname}${target.search}${target.hash}`;
          if (localUrl.startsWith('/login')) {
            void this.router.navigateByUrl(DEFAULT_AUTHENTICATED_ROUTE, navOptions);
            return;
          }
          void this.router.navigateByUrl(localUrl || DEFAULT_AUTHENTICATED_ROUTE, navOptions);
          return;
        }
      } catch {
        // Fallback to default route when returnUrl cannot be parsed safely.
      }
    }
    void this.router.navigateByUrl(DEFAULT_AUTHENTICATED_ROUTE, navOptions);
  }

  private hydrateFromStorage(): void {
    const access = this.tokens.getAccessToken();
    if (!access) {
      this.userSignal.set(null);
      return;
    }
    if (!isRecognizedAccessToken(access)) {
      this.tokens.clearAccessToken();
      this.userSignal.set(null);
      return;
    }
    this.userSignal.set(this.sessionFromAccessToken(access));
  }

  private persistTokens(tokens: AuthTokens): void {
    this.tokens.setAccessToken(tokens.accessToken);
    if (authUsesRefreshCookie(this.env)) {
      this.tokens.clearRefreshToken();
    } else if (tokens.refreshToken) {
      this.tokens.setRefreshToken(tokens.refreshToken);
    }
    this.userSignal.set(this.sessionFromAccessToken(tokens.accessToken));
  }

  /** Maps JWT claims to `SessionUser`. Role claims are UI hints only — see `jwt.util.ts`. */
  private sessionFromAccessToken(accessToken: string): SessionUser {
    const payload = decodeJwtPayload(accessToken);
    const rolesClaim = this.env.auth.jwtRolesClaim;
    const roles =
      payload !== null ? rolesFromPayload(payload, rolesClaim) : ([] as string[]);
    const sub = typeof payload?.['sub'] === 'string' ? payload['sub'] : undefined;
    const email = typeof payload?.['email'] === 'string' ? payload['email'] : undefined;
    return { sub, email, roles };
  }
}
